import React, { useEffect, useState } from "react";
import { Seo } from "@/components/Seo";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { Sprout, Users, MapPin, Copy, Code, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function OrgImpact() {
  const { orgId } = useParams();
  const [sp] = useSearchParams();
  const isEmbed = sp.get("embed") === "1"; // iframe mode — no site chrome extras
  const [d, setD] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setNotFound(false);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orgs/${orgId}/impact`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error("not found")))
      .then(setD)
      .catch(() => { setD(null); setNotFound(true); });
  }, [orgId]);

  if (notFound) {
    return (
      <div data-testid="impact-not-found" className="max-w-3xl mx-auto px-6 py-24 text-center">
        <div className="font-display text-3xl text-forest">Impact report not found</div>
        <p className="text-neutral-600 mt-3 text-sm">This organisation may no longer exist, or the link is wrong.</p>
        <Link to="/opportunities" data-testid="impact-not-found-back" className="inline-block mt-6 text-forest font-medium underline">Find opportunities</Link>
      </div>
    );
  }
  if (!d) return <div className="max-w-3xl mx-auto px-6 py-20 text-neutral-500">Loading impact…</div>;
  const o = d.org;
  const embed = `<iframe src="${window.location.origin}/o/${orgId}/impact?embed=1" width="100%" height="520" style="border:0;border-radius:14px"></iframe>`;
  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied"); };

  return (
    <div className="max-w-5xl mx-auto px-6 py-10" data-testid="org-impact-page">
      <Seo title="Impact report" />
      {!isEmbed && (
        <Link
          to={`/o/${orgId}`}
          data-testid="impact-back-btn"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-forest/70 hover:text-forest mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to {d.org?.org_name || d.org?.name || "organisation"}
        </Link>
      )}
      <Card className="border-neutral-200 overflow-hidden">
        <CardContent className="p-8 bg-gradient-to-br from-forest/[0.04] to-terracotta/[0.04]">
          <div className="text-xs uppercase tracking-[0.2em] text-forest/70 font-bold">Impact report</div>
          <h1 className="font-display text-4xl text-forest mt-2 tracking-tight">{o.org_name || o.name}</h1>
          {o.location && <div className="text-sm text-neutral-600 mt-1 inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-forest/60" />{o.location}</div>}
          {o.bio && <p className="text-neutral-700 leading-relaxed mt-4 max-w-2xl">{o.bio}</p>}
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-6">
        <Stat label="Hours mobilised" value={d.total_hours} icon={Sprout} />
        <Stat label="Volunteers" value={d.volunteers} icon={Users} />
        <Stat label="Opportunities" value={d.opportunities_posted} />
        <Stat label="Top causes" value={d.top_causes.length} />
      </div>

      <h2 className="font-display text-2xl text-forest mt-10 tracking-tight">Where the hours went</h2>
      <div className="flex flex-wrap gap-2 mt-4">
        {d.top_causes.map(c => (
          <Badge key={c.value} variant="outline" className="rounded-full border-forest/20 text-forest px-4 py-2 capitalize">
            {c.value} · {c.hours}h
          </Badge>
        ))}
        {d.top_causes.length === 0 && <div className="text-sm text-neutral-500">No approved hours yet.</div>}
      </div>

      <Card className="mt-10 border-neutral-200">
        <CardContent className="p-6">
          <div className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Share or embed</div>
          <div className="flex gap-2 mt-3 flex-wrap">
            <Button data-testid="impact-copy-link" variant="outline" onClick={() => copy(window.location.href)} className="rounded-full"><Copy className="w-4 h-4 mr-1.5" />Copy link</Button>
            <Button data-testid="impact-copy-embed" variant="outline" onClick={() => copy(embed)} className="rounded-full"><Code className="w-4 h-4 mr-1.5" />Copy embed code</Button>
          </div>
          <pre className="text-xs bg-neutral-50 border border-neutral-200 rounded-lg p-3 mt-3 overflow-x-auto">{embed}</pre>
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-6">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.18em] text-neutral-500">{label}</div>
        {Icon && <Icon className="w-4 h-4 text-forest/60" />}
      </div>
      <div className="font-display text-3xl text-forest mt-2">{value}</div>
    </div>
  );
}
